"use client";

import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import type { AnalyzeData } from "@/types/analyze";

interface MiniScoreChartsProps {
  analysisData: AnalyzeData;
}

export function MiniScoreCharts({ analysisData }: MiniScoreChartsProps) {
  const getScoreColor = (score: number) => {
    if (score >= 80) return "#10b981"; // green-500
    if (score >= 60) return "#f97316"; // orange-500
    return "#ef4444"; // red-500
  };

  const createChartData = (score: number) => [
    {
      name: "Score",
      value: score,
      fill: getScoreColor(score),
    },
    {
      name: "Remaining",
      value: 100 - score,
      fill: "transparent",
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
      {/* Website Quality Mini Donut */}
      <div className="text-center space-y-3">
        <div className="relative h-32 w-full mb-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={createChartData(
                  analysisData.report.siteAnalysisOverallScore ?? 0
                )}
                cx="50%"
                cy="50%"
                innerRadius={38}
                outerRadius={50}
                strokeWidth={0}
                cornerRadius={6}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                animationDuration={1000}
                animationEasing="ease-out"
              >
                {createChartData(
                  analysisData.report.siteAnalysisOverallScore ?? 0
                ).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="text-2xl font-bold tracking-tight text-foreground">
              {analysisData.report.siteAnalysisOverallScore ?? 0}
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-bold tracking-tight text-foreground">
            Website Quality
          </h3>
        </div>
      </div>

      {/* Trust & Security Mini Donut */}
      <div className="text-center space-y-3">
        <div className="relative h-32 w-full mb-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={createChartData(
                  analysisData.report.trustSecurityOverallScore ?? 0
                )}
                cx="50%"
                cy="50%"
                innerRadius={38}
                outerRadius={50}
                strokeWidth={0}
                cornerRadius={6}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                animationDuration={1000}
                animationEasing="ease-out"
              >
                {createChartData(
                  analysisData.report.trustSecurityOverallScore ?? 0
                ).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="text-2xl font-bold tracking-tight text-foreground">
              {analysisData.report.trustSecurityOverallScore ?? 0}
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-bold tracking-tight text-foreground">
            Trust & Security
          </h3>
        </div>
      </div>

      {/* PageSpeed Mini Donut */}
      <div className="text-center space-y-3">
        <div className="relative h-32 w-full mb-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={createChartData(analysisData.report.psiOverallScore ?? 0)}
                cx="50%"
                cy="50%"
                innerRadius={38}
                outerRadius={50}
                strokeWidth={0}
                cornerRadius={6}
                dataKey="value"
                startAngle={90}
                endAngle={-270}
                animationDuration={1000}
                animationEasing="ease-out"
              >
                {createChartData(analysisData.report.psiOverallScore ?? 0).map(
                  (entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  )
                )}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="text-2xl font-bold tracking-tight text-foreground">
              {analysisData.report.psiOverallScore ?? 0}
            </div>
          </div>
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-bold tracking-tight text-foreground">
            PageSpeed
          </h3>
        </div>
      </div>
    </div>
  );
}
